import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Select from 'react-select';
import { peopleApi, raciApi, listApi } from '../services/api';
import { useToast } from '../hooks/useToast';
import { useSwal } from '../hooks/useSwal';
import { NimbusTable } from '../components/NimbusTable';
import { NimbusModal, NimbusModalContent } from '../components/NimbusModal';
import { PageHeader } from '../components/PageHeader';
import { SortableTh } from '../components/SortableTh';
import { useSortableData } from '../hooks/useSortableData';
import { usePagination } from '../hooks/usePagination';
import { Pagination } from '../components/Pagination';
import { reactSelectStyles } from '../utils/reactSelectStyles';

type Person = {
  personId: string;
  firstName?: string;
  lastName?: string;
  personName?: string;
};

type Assignment = {
  raciId?: string;  // Current field name
  assignmentId?: string; // Legacy field name for backwards compatibility
  personId: string;
  role: 'R' | 'A' | 'C' | 'I' | string;
  notes?: string;
  riskId?: string;
  issueId?: string;
  incidentId?: string;
  remediationId?: string;
  evidenceId?: string;
  controlId?: string;
  documentId?: string;
};

type ArtefactType = 'risk' | 'issue' | 'incident' | 'remediation' | 'evidence' | 'control' | 'document';

type RaciColumn = 'type' | 'artefact' | 'person' | 'role' | 'notes';

const ARTEFACT_TYPE_OPTIONS: { value: ArtefactType; label: string }[] = [
  { value: 'risk', label: 'Risk' },
  { value: 'issue', label: 'Issue' },
  { value: 'incident', label: 'Incident' },
  { value: 'remediation', label: 'Remediation' },
  { value: 'evidence', label: 'Evidence' },
  { value: 'control', label: 'Control' },
  { value: 'document', label: 'Document' },
];

// Route segment for each artefact type's real record page — used to link the Artefact column.
const ARTEFACT_ROUTE_SEGMENT: Record<ArtefactType, string> = {
  risk: 'risks',
  issue: 'issues',
  incident: 'incidents',
  remediation: 'remediations',
  evidence: 'evidence',
  control: 'controls',
  document: 'documents',
};

const getPersonLabel = (p: Person): string => {
  const full = `${p.firstName || ''} ${p.lastName || ''}`.trim();
  return (full || p.personName || p.personId).trim();
};

// Controls are a dynamic register (Control.fields is a free-form bag, same shape ControlsList.tsx
// and ControlDetail.tsx already read) - controlId is the system-generated DynamoDB key, never
// meant for display. The human-given reference code and title live inside `fields`, at
// `control_id`/`control_title` - same field names ControlsList.tsx's own `get()` helper reads.
type ControlLite = { controlId?: string; fields?: Record<string, any> };
const getControlField = (c: ControlLite, fieldId: string): string => {
  const v = c.fields?.[fieldId];
  return v === undefined || v === null ? '' : String(v);
};

const getAssignmentArtefact = (a: Assignment): { type: ArtefactType | 'unknown'; id: string } => {
  if (a.riskId) return { type: 'risk', id: a.riskId };
  if (a.issueId) return { type: 'issue', id: a.issueId };
  if (a.incidentId) return { type: 'incident', id: a.incidentId };
  if (a.remediationId) return { type: 'remediation', id: a.remediationId };
  if (a.evidenceId) return { type: 'evidence', id: a.evidenceId };
  if (a.controlId) return { type: 'control', id: a.controlId };
  if (a.documentId) return { type: 'document', id: a.documentId };
  return { type: 'unknown', id: '' };
};

export const RaciAdminPage: React.FC = () => {
  const qc = useQueryClient();
  const toast = useToast();
  const swal = useSwal();

  const { data: people = [] } = useQuery<Person[]>({
    queryKey: ['people'],
    queryFn: peopleApi.getAll,
  });

  // Unscoped (no matrixId) - returns every control tenant-wide, which this page needs since an
  // assignment's control can belong to any matrix. Only used to resolve a display label; the
  // system controlId already fully identifies the record for linking/filtering/sorting.
  const { data: controls = [] } = useQuery<ControlLite[]>({
    queryKey: ['controls'],
    queryFn: listApi.controls,
  });

  const controlsById = useMemo(() => {
    const m = new Map<string, ControlLite>();
    controls.forEach((c) => c.controlId && m.set(c.controlId, c));
    return m;
  }, [controls]);

  /** "<given id> - <title>" - same convention LinkedArtefacts.tsx already uses for most non-
   * asset/person/document types. Falls back to the raw system id (today's behaviour) if the
   * control hasn't loaded yet or no longer exists, rather than showing nothing. */
  const getArtefactDisplay = (artefact: { type: ArtefactType | 'unknown'; id: string }): string => {
    if (artefact.type !== 'control') return artefact.id;
    const control = controlsById.get(artefact.id);
    if (!control) return artefact.id;
    const givenId = getControlField(control, 'control_id');
    const title = getControlField(control, 'control_title');
    if (!givenId && !title) return artefact.id;
    return [givenId, title].filter(Boolean).join(' - ');
  };

  const { data: assignments = [], isLoading, error } = useQuery<Assignment[]>({
    queryKey: ['raci'],
    queryFn: async () => {
      const result = await raciApi.getAll();
      return result;
    },
  });

  const peopleById = useMemo(() => {
    const m = new Map<string, Person>();
    people.forEach((p) => m.set(p.personId, p));
    return m;
  }, [people]);

  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState<ArtefactType | ''>('');

  const searched = useMemo(() => {
    const q = search.trim().toLowerCase();
    return assignments.filter((a) => {
      const artefact = getAssignmentArtefact(a);
      const person = peopleById.get(a.personId);
      const personLabel = person ? getPersonLabel(person) : a.personId;
      const matchesSearch = !q || artefact.id.toLowerCase().includes(q) || personLabel.toLowerCase().includes(q);
      const matchesType = !filterType || artefact.type === filterType;
      return matchesSearch && matchesType;
    });
  }, [assignments, search, filterType, peopleById]);

  const { sorted, field: sortField, dir: sortDir, toggle: toggleSort } = useSortableData<Assignment, RaciColumn>(
    searched,
    (a, f) => {
      switch (f) {
        case 'type': return getAssignmentArtefact(a).type;
        case 'artefact': return getAssignmentArtefact(a).id;
        case 'person': {
          const person = peopleById.get(a.personId);
          return person ? getPersonLabel(person) : a.personId;
        }
        case 'role': return a.role || '';
        case 'notes': return a.notes || '';
        default: return '';
      }
    },
    'type',
  );

  const { pageItems, page, setPage, totalPages, pageSize, totalItems } = usePagination(sorted, 25);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    artefactType: 'risk' as ArtefactType,
    artefactId: '',
    personId: '',
    role: 'R',
    notes: '',
  });

  const resetForm = () => {
    setEditingId(null);
    setForm({ artefactType: 'risk', artefactId: '', personId: '', role: 'R', notes: '' });
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (assignment: Assignment) => {
    beginEdit(assignment);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: any = {
        personId: form.personId,
        role: form.role,
        notes: form.notes || '',
      };
      payload[`${form.artefactType}Id`] = form.artefactId;

      if (editingId) {
        await raciApi.update(editingId, payload);
        return;
      }
      await raciApi.create(payload);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['raci'] });
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await raciApi.delete(id);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['raci'] });
      toast.success('Assignment deleted.');
    },
    onError: (e: Error) => toast.error(`Failed to delete: ${e.message}`),
  });

  const handleDelete = async (a: Assignment) => {
    const confirmed = await swal.confirmDelete('this assignment');
    if (!confirmed) return;
    const id = a.raciId || a.assignmentId;
    if (!id) { toast.error('Cannot delete: Assignment has no ID'); return; }
    deleteMutation.mutate(id);
  };

  const beginEdit = (a: Assignment) => {
    const artefact = getAssignmentArtefact(a);
    if (artefact.type === 'unknown') return;

    const id = a.raciId || a.assignmentId;
    if (!id) {
      console.error('No ID found for assignment:', a);
      toast.error('Cannot edit: Assignment has no ID');
      return;
    }
    setEditingId(id);
    setForm({
      artefactType: artefact.type,
      artefactId: artefact.id,
      personId: a.personId,
      role: (a.role || 'R') as any,
      notes: a.notes || '',
    });
  };

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-shield-user-line"
        title="RACI"
        actions={
          <>
            <a href="/raci-bulk" className="np-btn np-btn-ghost">
              <i className="ri-stack-line"></i>
              Bulk Assign
            </a>
            <button className="np-btn np-btn-primary" onClick={openCreateModal}>
              <i className="ri-add-line"></i>
              Create Assignment
            </button>
          </>
        }
      />

      <div className="np-card">
        <div className="np-card-body border-bottom py-3">
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <div className="np-search" style={{ maxWidth: 260 }}>
              <i className="ri-search-line"></i>
              <input
                type="text"
                placeholder="Search artefact ID or person…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button className="np-icon-btn" onClick={() => setSearch('')}>
                  <i className="ri-close-line"></i>
                </button>
              )}
            </div>
            <select className="np-select" style={{ maxWidth: 170 }} value={filterType} onChange={(e) => setFilterType(e.target.value as ArtefactType | '')}>
              <option value="">All Types</option>
              {ARTEFACT_TYPE_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            {(search || filterType) && (
              <button className="np-btn np-btn-ghost np-btn-sm" onClick={() => { setSearch(''); setFilterType(''); }}>
                <i className="ri-filter-off-line"></i>Clear
              </button>
            )}
            <span className="np-sub np-mb-0 ms-auto">{sorted.length} result{sorted.length !== 1 ? 's' : ''}</span>
          </div>
        </div>
        <div className="np-card-body">
          {isLoading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : error ? (
            <div className="np-banner np-tone-danger mb-0"><i className="ri-error-warning-line"></i>Failed to load assignments.</div>
          ) : sorted.length === 0 ? (
            <div className="text-center py-5">
              <i className="ri-inbox-line ri-48px d-block mb-2" style={{ color: 'var(--bs-secondary-color)' }}></i>
              <p className="np-sub mb-0">{assignments.length === 0 ? 'No assignments found.' : 'No results match your filters.'}</p>
            </div>
          ) : (
            <NimbusTable thead={<tr>
                    <SortableTh field="type" active={sortField === 'type'} dir={sortDir} onSort={toggleSort} width={130}>Type</SortableTh>
                    <SortableTh field="artefact" active={sortField === 'artefact'} dir={sortDir} onSort={toggleSort} width={220}>Artefact</SortableTh>
                    <SortableTh field="person" active={sortField === 'person'} dir={sortDir} onSort={toggleSort}>Person</SortableTh>
                    <SortableTh field="role" active={sortField === 'role'} dir={sortDir} onSort={toggleSort} width={80}>Role</SortableTh>
                    <SortableTh field="notes" active={sortField === 'notes'} dir={sortDir} onSort={toggleSort}>Notes</SortableTh>
                    <th style={{ width: '180px' }}></th>
                  </tr>}>
                  {pageItems.map((a, index) => {
                    const artefact = getAssignmentArtefact(a);
                    const person = peopleById.get(a.personId);
                    const assignmentId = a.raciId || a.assignmentId || `assignment-${index}`;
                    return (
                      <tr key={assignmentId}>
                        <td>{artefact.type}</td>
                        <td className="fw-medium">
                          {artefact.type !== 'unknown' && artefact.id ? (
                            <Link to={`/${ARTEFACT_ROUTE_SEGMENT[artefact.type]}/${artefact.id}`}>{getArtefactDisplay(artefact)}</Link>
                          ) : (
                            artefact.id || '-'
                          )}
                        </td>
                        <td>{person ? getPersonLabel(person) : a.personId}</td>
                        <td>{a.role}</td>
                        <td className="np-sub">{a.notes || ''}</td>
                        <td>
                          <div className="d-flex gap-1">
                            <button
                              className="np-icon-btn"
                              onClick={() => openEditModal(a)}
                              title="Edit"
                            >
                              <i className="ri-edit-line"></i>
                            </button>
                            <button
                              className="np-icon-btn np-icon-btn-danger"
                              onClick={() => handleDelete(a)}
                              disabled={deleteMutation.isPending}
                              title="Delete"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
            </NimbusTable>
          )}
          {pageItems.length > 0 && (
            <Pagination page={page} totalPages={totalPages} pageSize={pageSize} totalItems={totalItems} onPageChange={setPage} />
          )}
        </div>
      </div>

      {showModal && (
        <NimbusModal>
          <NimbusModalContent
            title={editingId ? 'Edit Assignment' : 'Add Assignment'}
            onClose={closeModal}
            footer={
              <>
                <button type="button" className="np-btn np-btn-ghost" onClick={closeModal}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="np-btn np-btn-primary"
                  onClick={() => { saveMutation.mutate(); closeModal(); }}
                  disabled={saveMutation.isPending || !form.artefactId.trim() || !form.personId}
                >
                  {saveMutation.isPending ? 'Saving…' : editingId ? 'Save Changes' : 'Create Assignment'}
                </button>
              </>
            }
          >
                <div className="np-field">
                  <label>Type</label>
                  <select className="np-select" value={form.artefactType} onChange={(e) => setForm((f) => ({ ...f, artefactType: e.target.value as ArtefactType }))}>
                    {ARTEFACT_TYPE_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div className="np-field">
                  <label>Artefact ID</label>
                  <input className="np-input" value={form.artefactId} onChange={(e) => setForm((f) => ({ ...f, artefactId: e.target.value }))} placeholder="e.g. RISK-123" />
                </div>
                <div className="np-field">
                  <label>Person</label>
                  <Select
                    options={people.map((p) => ({ value: p.personId, label: getPersonLabel(p) }))}
                    value={(() => {
                      const opt = people.find((p) => p.personId === form.personId);
                      return opt ? { value: opt.personId, label: getPersonLabel(opt) } : null;
                    })()}
                    onChange={(opt) => setForm((f) => ({ ...f, personId: opt?.value ?? '' }))}
                    placeholder="Select person…"
                    isClearable
                    isSearchable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={reactSelectStyles()}
                  />
                </div>
                <div className="np-field">
                  <label>Role</label>
                  <select className="np-select" value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}>
                    <option value="R">R</option>
                    <option value="A">A</option>
                    <option value="C">C</option>
                    <option value="I">I</option>
                  </select>
                </div>
                <div className="np-field">
                  <label>Notes</label>
                  <input className="np-input" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
                </div>
          </NimbusModalContent>
        </NimbusModal>
      )}
    </div>
  );
};
